import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileText, Download, Clock, Send, AlertTriangle, CheckCircle, Layers, User, MessageSquare, ToggleLeft, ToggleRight } from "lucide-react";
import API from "@/lib/api";

function TimeRemaining({ expiresAt }) {
  const [timeStr, setTimeStr] = useState("");
  useEffect(() => {
    const calc = () => {
      const diff = new Date(expiresAt) - new Date();
      if (diff <= 0) { setTimeStr("Expired"); return; }
      const hrs = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      setTimeStr(hrs > 0 ? `${hrs}h ${mins}m remaining` : `${mins}m remaining`);
    };
    calc();
    const i = setInterval(calc, 60000);
    return () => clearInterval(i);
  }, [expiresAt]);
  const expired = new Date(expiresAt) < new Date();
  return (
    <span className={`text-xs font-medium flex items-center gap-1 ${expired ? "text-red-400" : "text-amber-400"}`}>
      <Clock className="w-3.5 h-3.5" />{timeStr}
    </span>
  );
}

export default function PricingRequestProForma() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [estimates, setEstimates] = useState({});
  const [generalNotes, setGeneralNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [useDays, setUseDays] = useState(false);
  const [hoursPerDay, setHoursPerDay] = useState(8);

  useEffect(() => {
    API.get(`/p/${token}`)
      .then(r => {
        setData(r.data);
        if (r.data.hours_per_day) setHoursPerDay(r.data.hours_per_day);
        if (r.data.existing_response) {
          setEstimates(r.data.existing_response.estimates || {});
          setGeneralNotes(r.data.existing_response.respondent_notes || "");
        }
        if (r.data.status === "Submitted") setSubmitted(true);
      })
      .catch(e => {
        const status = e.response?.status;
        if (status === 404) setError("This link is not valid or has been revoked.");
        else if (status === 410) setError("This link has expired. Please contact the sender for a new link.");
        else if (status === 403) setError("This request has not been sent yet.");
        else setError("Unable to load. Please try again or contact the sender.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const stages = useMemo(() => {
    if (!data?.items) return [];
    const stageMap = {};
    data.items.forEach(item => {
      if (!stageMap[item.stage]) stageMap[item.stage] = [];
      stageMap[item.stage].push(item);
    });
    return Object.entries(stageMap).sort((a, b) => a[0].localeCompare(b[0]));
  }, [data]);

  const targetRole = data?.target_role || (data?.roles?.[0] || "");

  const toDisplay = (hrs) => useDays ? (hrs / hoursPerDay) : hrs;
  const fromDisplay = (val) => useDays ? (val * hoursPerDay) : val;
  const unit = useDays ? "days" : "hrs";

  const getHours = (itemId) => estimates[itemId]?.hours?.[targetRole] || 0;

  const setHours = (itemId, displayVal) => {
    const num = displayVal === "" ? 0 : parseFloat(displayVal) || 0;
    const hours = fromDisplay(num);
    setEstimates(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], hours: { ...(prev[itemId]?.hours || {}), [targetRole]: Math.round(hours * 100) / 100 }, notes: prev[itemId]?.notes || "" }
    }));
  };

  const setItemNotes = (itemId, notes) => {
    setEstimates(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], hours: prev[itemId]?.hours || {}, notes }
    }));
  };

  const totalHours = useMemo(() => {
    if (!data?.items) return 0;
    const displayedIds = new Set(data.items.map(i => i.id));
    let total = 0;
    Object.entries(estimates).forEach(([itemId, est]) => {
      if (displayedIds.has(itemId)) {
        total += est.hours?.[targetRole] || 0;
      }
    });
    return total;
  }, [estimates, targetRole, data]);

  const handleSubmit = async () => {
    const displayedIds = new Set(data?.items?.map(i => i.id) || []);
    const filteredEstimates = {};
    Object.entries(estimates).forEach(([itemId, est]) => {
      if (displayedIds.has(itemId)) {
        filteredEstimates[itemId] = est;
      }
    });
    const filledItems = Object.entries(filteredEstimates).filter(([, est]) =>
      (est.hours?.[targetRole] || 0) > 0
    );
    if (filledItems.length === 0) {
      toast.error("Please enter time for at least one deliverable");
      return;
    }
    setSubmitting(true);
    try {
      await API.post(`/p/${token}/submit`, {
        estimates: filteredEstimates,
        general_notes: generalNotes,
        respondent_name: data.recipient_name,
      });
      setSubmitted(true);
      toast.success("Estimates submitted successfully");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadFile = (fileId, filename) => {
    const url = `${API.defaults.baseURL}/p/${token}/files/${fileId}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      <Card className="bg-slate-900 border-slate-800 max-w-md w-full">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">Link Unavailable</h2>
          <p className="text-sm text-slate-400">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      <Card className="bg-slate-900 border-slate-800 max-w-lg w-full">
        <CardContent className="p-8 text-center">
          <CheckCircle className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Estimates Submitted</h2>
          <p className="text-sm text-slate-400 mb-4">
            Your <strong className="text-slate-200">{targetRole}</strong> effort estimates for <strong className="text-slate-200">{data.discipline}</strong> on <strong className="text-slate-200">{data.project_name}</strong> have been submitted.
          </p>
          <p className="text-xs text-slate-500">
            The project lead has been notified. You may close this page.
          </p>
        </CardContent>
      </Card>
    </div>
  );

  const brandName = data?.branding?.company_name || "MEP.Engine";
  const formatSize = (bytes) => bytes > 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;

  return (
    <div className="min-h-screen bg-slate-950 py-6 px-4" data-testid="pricing-pro-forma">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-1">Effort Estimate — Pro Forma</p>
            <h1 className="text-2xl font-bold text-white tracking-tight">{data.project_name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs border-blue-500/30 text-blue-400">{data.discipline}</Badge>
              <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">{targetRole}</Badge>
            </div>
          </div>
          <TimeRemaining expiresAt={data.expires_at} />
        </div>
      </div>

      <div className="max-w-4xl mx-auto space-y-5">
        {/* Info bar */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <User className="w-4 h-4 text-slate-500" />
              <span className="text-sm text-slate-300">{data.recipient_name}</span>
              <span className="text-slate-600">|</span>
              <span className="text-xs text-slate-500">Requested by {data.created_by_name}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">{data.items?.length || 0} deliverables</span>
              {/* Hours/Days toggle */}
              <button onClick={() => setUseDays(!useDays)} className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-600 transition-colors" data-testid="toggle-hrs-days">
                {useDays ? <ToggleRight className="w-3.5 h-3.5 text-blue-400" /> : <ToggleLeft className="w-3.5 h-3.5 text-slate-500" />}
                {useDays ? "Days" : "Hours"}
              </button>
              {useDays && (
                <div className="flex items-center gap-1">
                  <span className="inline-flex items-center justify-center w-12 h-7 text-center text-xs bg-slate-800/60 border border-slate-700 text-slate-400 rounded-md font-mono" data-testid="hours-per-day">{hoursPerDay}</span>
                  <span className="text-[10px] text-slate-500">hrs/day</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Message from sender */}
        {data.message && (
          <Card className="bg-blue-950/20 border-blue-500/20">
            <CardContent className="p-4 flex items-start gap-3">
              <MessageSquare className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-blue-400 mb-1">Note from {data.created_by_name}</p>
                <p className="text-sm text-slate-300">{data.message}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reference Files */}
        {data.reference_files?.length > 0 && (
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader className="py-3 px-4 border-b border-slate-800/50">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-400" />Reference Files ({data.reference_files.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-2">
              {data.reference_files.map(f => (
                <div key={f.id} className="flex items-center justify-between bg-slate-800/40 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                    <span className="text-sm text-slate-300 truncate">{f.filename}</span>
                    <span className="text-xs text-slate-600">{formatSize(f.size_bytes)}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDownloadFile(f.id, f.filename)} className="text-blue-400 hover:text-blue-300" data-testid={`download-file-${f.id}`}>
                    <Download className="w-3.5 h-3.5 mr-1" />Download
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Deliverables by Stage — single role column */}
        {stages.map(([stageName, items]) => (
          <Card key={stageName} className="bg-slate-900/60 border-slate-800">
            <CardHeader className="py-3 px-4 border-b border-slate-800/50">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Layers className="w-4 h-4 text-slate-400" />{stageName}
                <Badge variant="outline" className="text-xs border-slate-700 text-slate-500 ml-2">{items.length} items</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-800/30">
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 px-3 min-w-[240px]">Deliverable</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 px-2 text-center w-28">
                        {targetRole} ({unit})
                      </TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 px-2 w-56">Notes / Assumptions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map(item => (
                      <TableRow key={item.id} className="border-slate-800/50 hover:bg-slate-800/20">
                        <TableCell className="px-3 py-2.5">
                          <div>
                            <p className="text-sm font-medium text-slate-200">{item.name}</p>
                            {item.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{item.description}</p>}
                            {item.quantity > 1 && <Badge variant="outline" className="mt-1 text-xs border-slate-700 text-slate-500">Qty: {item.quantity}</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="px-2 py-2.5 text-center">
                          <Input
                            type="number"
                            min="0"
                            step={useDays ? "0.5" : "1"}
                            value={getHours(item.id) > 0 ? toDisplay(getHours(item.id)).toFixed(useDays ? 1 : 0) : ""}
                            onChange={e => setHours(item.id, e.target.value)}
                            className="w-24 h-8 text-center text-sm bg-slate-800 border-slate-700 text-slate-200 mx-auto"
                            placeholder="0"
                            data-testid={`hours-${item.id}`}
                          />
                        </TableCell>
                        <TableCell className="px-2 py-2.5">
                          <Input
                            value={estimates[item.id]?.notes || ""}
                            onChange={e => setItemNotes(item.id, e.target.value)}
                            className="h-8 text-xs bg-slate-800 border-slate-700 text-slate-300"
                            placeholder="Notes or assumptions..."
                            data-testid={`notes-${item.id}`}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* General Notes & Submit */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4 space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block mb-2">General Notes & Assumptions</label>
              <Textarea
                value={generalNotes}
                onChange={e => setGeneralNotes(e.target.value)}
                placeholder="Add any general assumptions, caveats, or notes about your estimates..."
                className="bg-slate-800 border-slate-700 text-slate-200 min-h-[80px]"
                data-testid="general-notes"
              />
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-slate-800">
              <div className="text-sm text-slate-400">
                Total: <span className="font-semibold text-white">{toDisplay(totalHours).toFixed(useDays ? 1 : 0)} {unit}</span>
                {useDays && <span className="text-xs text-slate-600 ml-1">({totalHours.toFixed(0)} hrs)</span>}
              </div>
              <Button onClick={handleSubmit} disabled={submitting || totalHours === 0} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-estimates-btn">
                {submitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Submit Estimates
              </Button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-600 pb-6">
          This link is confidential and intended for the named recipient only.
        </p>
      </div>
    </div>
  );
}
